import classnames from 'classnames';
import React, { FC, useCallback, useMemo, useRef } from 'react';
import { ActionName, BiAddMediaActionTarget } from '../../../../constants/Bi';
import { ContextMenuId } from '../../../../constants/ContextMenuId';
import { callOrUse } from '../../../../utils/callOrUse';
import { stopPropagation } from '../../../../utils/dom/domEvents';
import { useTrackActions } from '../../../../utils/hooks/bi/useTrackActions';
import { ActionClickOrigin } from '../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import { useTrackAddMediaMediaSourceSelected } from '../../../../utils/hooks/bi/useTrackAddMediaMediaSourceSelected';
import { MediaManagerSDKAnyItem } from '../../../../utils/media-manager-sdk';
import { isNativeFile } from '../../../../utils/media-manager-sdk/items';
import { isTranscoding } from '../../../../utils/media-manager-sdk/transcoding';
import { ContextMenuTrigger } from '../../../common';
import { LassoItem } from '../../../common/Lasso/LassoItem';
import { IGenericItem } from '../../types';
import styles from './GenericItem.scss';
import { useContextMenuHandlers, useMouseHandlers, useScrollHandler } from './hooks';

export const GenericItem: FC<IGenericItem> = ({
  renderTag = 'li',
  item,
  selectedItems = [],
  children,
  className,
  itemClassName,
  lassoItemClassName,
  onNameChange,
  onRenameComplete,
  onRenameCancel,
  onClick,
  onContextMenu,
  onDoubleClick,
  noContextMenu,
  dataHook = 'generic-item',
  dataHookId,
  contextMenuId = ContextMenuId.SelectedItems,
  canDrag,
  style,
  selected,
  onDrop,
  isEditing,
  isRelated,
}) => {
  const itemRef = useRef(null);
  useScrollHandler(item, itemRef);

  const { contextTriggerRef, getParamsForItemContextMenu, openContextMenu } = useContextMenuHandlers({
    item,
    selectedItems,
  });

  const { trackAddMediaMediaSourceSelectedDragAndDrop } = useTrackAddMediaMediaSourceSelected();

  const { handleClick, handleContextMenu, handleDoubleClick } = useMouseHandlers(item, {
    onClick,
    onContextMenu,
    onDoubleClick,
  });

  const { setLastActionParams, trackActionsDone } = useTrackActions();

  const handleDropOnFolder = useCallback(
    (files: MediaManagerSDKAnyItem[], item, options) => {
      if (isNativeFile(files[0])) {
        trackAddMediaMediaSourceSelectedDragAndDrop(BiAddMediaActionTarget.FolderDragAndDrop);
      } else {
        setLastActionParams(files as MediaManagerSDKAnyItem[], { clickOrigin: ActionClickOrigin.FolderDragAndDrop });
        trackActionsDone(files, ActionName.MoveTo);
      }

      onDrop(files, item, options);
    },
    [trackAddMediaMediaSourceSelectedDragAndDrop, onDrop, setLastActionParams, trackActionsDone],
  );

  const handleNameChange = useCallback((event) => onNameChange(event.target.value), [onNameChange]);
  const handleRenameCancel = useCallback(() => {
    onNameChange(item.name);
    onRenameCancel();
  }, [item.name, onRenameCancel, onNameChange]);

  const attributes = useMemo(() => ({ className, style }), [className, style]);

  return (
    <ContextMenuTrigger
      renderTag={renderTag}
      id={contextMenuId}
      disable={noContextMenu}
      attributes={attributes}
      ref={contextTriggerRef}
      holdToDisplay={-1}
      collect={getParamsForItemContextMenu}
    >
      <div
        className={classnames(styles.item, itemClassName)}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        onMouseDown={stopPropagation}
        data-generic-item-wrapper-div="true"
        data-generic-item-name={item.name}
        data-hook={dataHook}
        data-hook-selected={selected}
        data-hook-item-id={dataHookId ?? item.id}
        data-hook-item-type={isRelated ? 'related_item' : 'item'}
        ref={itemRef}
      >
        <LassoItem className={lassoItemClassName} itemId={dataHookId ?? item.id}>
          {callOrUse(children, {
            handleNameChange,
            handleRenameComplete: onRenameComplete,
            handleRenameCancel,
            handleDropOnFolder,
            openContextMenu,
            canDrag: canDrag && !isEditing && !isTranscoding(item),
          })}
        </LassoItem>
      </div>
    </ContextMenuTrigger>
  );
};
